import React from 'react'
import {
  Box,
  Typography,
  Paper,
  Container,
  Button,
  Fade,
  Grow,
  useTheme,
  alpha,
} from '@mui/material'
import {
  VerifiedUser,
  Schedule,
  ContactSupport,
  InfoOutlined,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

interface ProviderNotVerifiedPageProps {
  providerName?: string
}

/**
 * Professional UI component that displays when a provider's profile
 * has not been verified by the administrator.
 * This component is shown instead of protected content.
 */
const ProviderNotVerifiedPage: React.FC<ProviderNotVerifiedPageProps> = ({
  providerName,
}) => {
  const theme = useTheme()
  const navigate = useNavigate()

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          minHeight: '70vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
        }}
      >
        <Fade in timeout={800}>
          <Paper
            elevation={0}
            sx={{
              maxWidth: 700,
              width: '100%',
              borderRadius: 4,
              overflow: 'hidden',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
            }}
          >
            {/* Header Section with Icon */}
            <Box
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                p: 5,
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -50,
                  right: -50,
                  width: 200,
                  height: 200,
                  borderRadius: '50%',
                  background: alpha(theme.palette.common.white, 0.1),
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -30,
                  left: -30,
                  width: 150,
                  height: 150,
                  borderRadius: '50%',
                  background: alpha(theme.palette.common.white, 0.1),
                },
              }}
            >
              <Grow in timeout={1000}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    p: 2.5,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.common.white, 0.95),
                    mb: 2,
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  <Schedule
                    sx={{
                      fontSize: 56,
                      color: theme.palette.primary.main,
                    }}
                  />
                </Box>
              </Grow>

              <Typography
                variant="h4"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  mb: 1,
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                Profile Verification Pending
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: alpha(theme.palette.common.white, 0.95),
                  fontSize: '1.05rem',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                Your account is currently under review
              </Typography>
            </Box>

            {/* Content Section */}
            <Box sx={{ p: 5 }}>
              {providerName && (
                <Fade in timeout={1200}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 3,
                      color: 'text.primary',
                      fontWeight: 500,
                    }}
                  >
                    Hello {providerName},
                  </Typography>
                </Fade>
              )}

              <Fade in timeout={1400}>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 3,
                    color: 'text.secondary',
                    lineHeight: 1.7,
                    fontSize: '1rem',
                  }}
                >
                  Thank you for your patience. Our administrative team is
                  currently reviewing your provider profile and credentials to
                  ensure compliance with our platform standards and regulatory
                  requirements.
                </Typography>
              </Fade>

              <Fade in timeout={1600}>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 4,
                    color: 'text.secondary',
                    lineHeight: 1.7,
                    fontSize: '1rem',
                  }}
                >
                  Access to certain platform features, including job listings,
                  service packages, and PillarMatch, will be enabled once your
                  profile verification is complete.
                </Typography>
              </Fade>

              {/* Information Cards */}
              <Box sx={{ mb: 4 }}>
                <Grow in timeout={1800}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      mb: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.05),
                      border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                      borderRadius: 2,
                      display: 'flex',
                      gap: 2,
                    }}
                  >
                    <InfoOutlined
                      sx={{
                        color: theme.palette.info.main,
                        fontSize: 24,
                        flexShrink: 0,
                        mt: 0.5,
                      }}
                    />
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          mb: 0.5,
                          color: theme.palette.info.dark,
                        }}
                      >
                        What happens next?
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.6,
                        }}
                      >
                        Our team typically completes profile reviews within
                        24-48 business hours. You will receive an email
                        notification once your profile has been approved.
                      </Typography>
                    </Box>
                  </Paper>
                </Grow>

                <Grow in timeout={2000}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      bgcolor: alpha(theme.palette.success.main, 0.05),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                      borderRadius: 2,
                      display: 'flex',
                      gap: 2,
                    }}
                  >
                    <VerifiedUser
                      sx={{
                        color: theme.palette.success.main,
                        fontSize: 24,
                        flexShrink: 0,
                        mt: 0.5,
                      }}
                    />
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          mb: 0.5,
                          color: theme.palette.success.dark,
                        }}
                      >
                        What you can do now
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.6,
                        }}
                      >
                        While waiting for verification, you can complete your
                        profile, update your credentials, and familiarize
                        yourself with the platform features available in your
                        account settings.
                      </Typography>
                    </Box>
                  </Paper>
                </Grow>
              </Box>
            </Box>
          </Paper>
        </Fade>
      </Box>
    </Container>
  )
}

export default ProviderNotVerifiedPage
